import _ from "lodash";
import React from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import * as CommandPrimitive from "cmdk";
import * as ScrollArea from "@radix-ui/react-scroll-area";

import {
  useCmdHotKey,
  useSelectedOrganization,
} from "@/components/common/OrganizationSwitcher/hooks";
import { Avatar } from "@/components/ui/Avatar";
import { Scrollbar } from "@/components/ui/Scrollbar";
import { Spinner } from "@/components/ui/Spinner";
import {
  ChevronRight20,
  ExpandLess20,
  ExpandMore20,
  Language24,
  Logout20,
  MoreVert24,
} from "@/components/ui/SvgIcon";

import { useOrganizationsQuery } from "@/features/auth/queries/useOrganizationsQuery";

import cn from "@/utils/cn";
import { user } from "@/utils/firebase/user";
import { ReactUtils } from "@/utils/react";
import { ROUTES } from "@/constants/routes";

import { LanguageSwitcher } from "../LanguageSwitcher";

import { Organization } from "./types";
import { getMenuComponents } from "./utils";

type OrganizationSwitcherProps = {
  isCollapsed?: boolean;
};

export const OrganizationSwitcher = ({
  isCollapsed = false,
}: OrganizationSwitcherProps) => {
  const intl = useIntl();
  const [value, setValue] = React.useState("");
  const [search, setSearch] = React.useState("");
  const { data, isLoading } = useOrganizationsQuery();
  const organizations: Organization[] = _.get(data, "organization", []);
  const shouldUseCmd = organizations.length >= 5;
  const {
    Component,
    ComponentItem,
    CommandComponent,
    GroupComponent,
    ListComponent,
  } = getMenuComponents(shouldUseCmd);
  const [isOpen, setIsOpen] = React.useState(false);
  useCmdHotKey({ shouldUseCmd, setIsOpen });

  const filteredOrganizations = _(organizations)
    .uniqBy("name")
    .filter((item) => item.status === "ACTIVE")
    .filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
    .sortBy([
      // Sort org names that are an exact match first, then those that start with the search string, then the rest of the results.
      (item) => item.name.toLowerCase() !== search.toLowerCase(),
      (item) => !item.name.toLowerCase().startsWith(search.toLowerCase()),
      (item) => item.name.toLowerCase(),
    ])
    .value();

  const [selectedOrg, setSelectedOrg] = useSelectedOrganization();

  const handleSelectOrg = (org: Organization) => async () => {
    let redirectRoute = ROUTES.ASSETS;
    for (const route of Object.values(ROUTES)) {
      if (window.location.pathname.startsWith(route)) {
        redirectRoute = route;
      }
    }

    if (selectedOrg && selectedOrg.orgId === org.orgId) {
      setSelectedOrg(undefined);
      ReactUtils.hardRedirect(redirectRoute);
      return;
    }
    setSelectedOrg(org);
    ReactUtils.hardRedirect(redirectRoute);
  };

  function handleLogout() {
    user.signOut();
  }

  return (
    <Component.Root open={isOpen} onOpenChange={(o: boolean) => setIsOpen(o)}>
      <Component.Trigger
        id="organization-switcher-trigger"
        asChild
        disabled={isLoading}
        className="w-full rounded-lg outline-none disabled:cursor-not-allowed md:hover:bg-chorus-black-300 md:focus:bg-chorus-black-300"
      >
        <button
          className={cn(
            "flex items-start text-left transition-all duration-300 md:items-center",
            isCollapsed
              ? "w-full justify-between md:px-2 md:py-2"
              : "md:py-2 md:pl-4 md:pr-[7px]",
          )}
          data-testid="organization-switcher-trigger-button"
          title={isCollapsed ? selectedOrg?.name || "-" : undefined}
        >
          <Avatar name={selectedOrg?.name || "-"} />
          {isCollapsed ? (
            <div className="flex size-8 items-center justify-center max-md:hidden">
              {isLoading ? (
                <Spinner
                  size="sm"
                  data-testid="organization-switcher-spinner"
                />
              ) : (
                <MoreVert24 className="fill-chorus-gray-500" />
              )}
            </div>
          ) : (
            <>
              <div className="ml-2 flex flex-col items-start font-medium max-md:pb-0.5">
                <div className="text-xxs text-chorus-gray-600 md:text-chorus-gray-500">
                  <span>
                    <FormattedMessage
                      defaultMessage="Organization"
                      description="Label shown above currently selected organization name in organization switcher"
                    />
                  </span>
                </div>
                <div className="text-sm text-chorus-black-200 max-md:mr-4 max-md:inline-block max-md:leading-[1.3] md:text-chorus-gray-200">
                  {selectedOrg?.name || "-"}
                  {isOpen ? (
                    <ExpandLess20 className="-mr-5 inline fill-chorus-gray-700 md:hidden" />
                  ) : (
                    <ExpandMore20 className="-mr-5 inline fill-chorus-gray-700 md:hidden" />
                  )}
                </div>
              </div>
              <div className="ml-auto flex size-8 items-center justify-center self-start max-md:hidden">
                {isLoading ? (
                  <Spinner
                    size="sm"
                    data-testid="organization-switcher-spinner"
                  />
                ) : (
                  <MoreVert24 className="fill-chorus-gray-500" />
                )}
              </div>
            </>
          )}
        </button>
      </Component.Trigger>
      <Component.Portal>
        <Component.Content
          className="z-[999999] w-[256px] overflow-hidden rounded-lg border border-[#E2E8F0] bg-white radix-side-top:animate-slide-up md:border-chorus-black-300 md:bg-chorus-black-500"
          sideOffset={8}
          align="start"
        >
          {/* <Account> */}
          <div className="px-4 pb-[13px] pt-[15px]">
            <div className="text-xxs font-bold uppercase text-chorus-gray-600">
              <FormattedMessage
                defaultMessage="Account"
                description="Label for section containing user account email"
              />
            </div>
            <div
              className="truncate text-xs font-medium text-chorus-black-100 md:text-chorus-gray-300"
              title="longdevname@mayvenstudios.com"
            >
              {user.email}
            </div>
          </div>
          {/* </Account> */}
          {/* <Language Selector> */}
          <div className="flex w-full items-center border-y border-[#E2E8F0] pb-[15px] pl-4 pr-3 pt-[13px] outline-none md:border-chorus-black-200">
            <Language24 className="fill-chorus-teal-500" />
            <div className="pl-1 text-xs font-bold uppercase text-chorus-gray-600">
              <FormattedMessage
                defaultMessage="Language"
                description="Label for language selection dropdown in organization switcher"
              />
            </div>
            <div className="ml-auto">
              <LanguageSwitcher variant="orgSwitcher" />
            </div>
          </div>
          {/* </Language Selector> */}
          {/* <Organizations> */}
          {organizations.length && (
            <CommandComponent
              value={value}
              onValueChange={setValue}
              shouldFilter={false}
            >
              <div className="border-y border-[#E2E8F0] md:border-chorus-black-200">
                <div className="mb-[6px] flex justify-between pl-4 pt-[15px] text-xxs font-bold uppercase text-chorus-gray-600">
                  <div>
                    <FormattedMessage
                      defaultMessage="Organizations"
                      description="Header label for list of organizations user has access to"
                    />
                  </div>
                  {shouldUseCmd && (
                    <CommandPrimitive.CommandInput
                      placeholder={intl.formatMessage(
                        defineMessage({
                          defaultMessage: "Search...",
                          description:
                            "Placeholder text in search box for filtering organizations list",
                        }),
                      )}
                      autoFocus
                      className={cn(
                        "ml-auto w-[60px] border-none border-b-chorus-black-100 bg-transparent p-0 pr-4 text-[11px] font-normal leading-[11px] text-chorus-black-300 ring-white placeholder:text-chorus-gray-700 focus:border-chorus-black-100 focus:ring-0 focus-visible:shadow-none md:text-chorus-gray-300",
                        {
                          "opacity-0 pointer-events-none": !shouldUseCmd,
                        },
                      )}
                      onValueChange={(val) => setSearch(val)}
                      value={search}
                    />
                  )}
                </div>
                <ListComponent>
                  <ScrollArea.Root
                    type="auto"
                    className="mb-[11px] mt-[6px] h-[168px] w-full overflow-hidden [&_.bg-chorus-black-200]:bg-[#E2E8F0]"
                  >
                    <ScrollArea.Viewport
                      className="size-full overscroll-contain"
                      onWheel={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {shouldUseCmd && (
                        <CommandPrimitive.CommandEmpty className="px-2 py-5 text-center text-xxs text-chorus-gray-500">
                          <FormattedMessage
                            defaultMessage="No results"
                            description="Message shown when no organizations match the search criteria in organization switcher"
                          />
                        </CommandPrimitive.CommandEmpty>
                      )}
                      <GroupComponent>
                        {filteredOrganizations.map((item) => (
                          <ComponentItem
                            key={item.name}
                            className={cn(
                              "flex w-full cursor-pointer items-center outline-none md:focus:bg-chorus-black-300/30 md:aria-selected:bg-chorus-black-300/30",
                            )}
                            onSelect={handleSelectOrg(item)}
                          >
                            <OrganizationSwitcherItem
                              name={item.name}
                              isSelected={item.orgId === selectedOrg?.orgId}
                            />
                          </ComponentItem>
                        ))}
                      </GroupComponent>
                    </ScrollArea.Viewport>
                    <Scrollbar variant="dark" size="fat" />
                  </ScrollArea.Root>
                </ListComponent>
              </div>
            </CommandComponent>
          )}
          {/* </Organizations> */}
          {/* <Logout> */}
          <button
            className="flex w-full cursor-pointer items-center justify-between pb-[15px] pl-4 pr-3 pt-[13px] outline-none md:hover:bg-chorus-black-300"
            onClick={handleLogout}
            data-testid="organization-switcher-logout"
          >
            <div className="text-xs font-medium text-[#64748B] md:text-chorus-gray-200">
              <FormattedMessage
                defaultMessage="Log out"
                description="Button text that signs out the user from the application"
              />
            </div>
            <Logout20 className="fill-chorus-gray-700" />
          </button>
          {/* </Logout> */}
        </Component.Content>
      </Component.Portal>
    </Component.Root>
  );
};

type Props = {
  name: string;
  isSelected?: boolean;
};
const OrganizationSwitcherItem = ({ name, isSelected }: Props) => {
  return (
    <div
      data-testid={name}
      className="flex w-full cursor-pointer items-center py-2 pl-4 pr-3 outline-none focus:bg-chorus-black-300"
    >
      <Avatar
        name={name}
        checked={isSelected}
        className={cn({
          "bg-transparent border-chorus-gray-300 text-chorus-gray-500":
            !isSelected,
        })}
      />
      <div
        className={cn(
          "ml-2 truncate text-xs font-medium md:text-chorus-gray-200",
          !isSelected
            ? "max-md:text-chorus-gray-700"
            : "max-md:text-chorus-black-100",
        )}
      >
        {name}
      </div>
      {!isSelected && (
        <div className="ml-auto">
          <ChevronRight20 className="fill-chorus-gray-700" />
        </div>
      )}
    </div>
  );
};
